import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService, SignupRequest, SignupResponse } from '../../services/auth.service';
import { ApiError } from '../../services/api.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})

export class SignupComponent implements OnInit {
  signupForm: FormGroup;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private router: Router
  ) {
    this.signupForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      address: [''],
      addressLine2: [''],
      city: [''],
      state: [''],
      zip: [''],
      phone: [''],
      email: [''],
      adminEmail: ['', [Validators.required, Validators.email]],
      adminPassword: ['', [Validators.required, Validators.minLength(8)]],
      adminFirstName: [''],
      adminLastName: ['']
    });
  }

  ngOnInit(): void {}

  onSubmit(): void {
    if (this.signupForm.valid) {
      this.loading = true;

      // Prepare the request data
      const signupData: SignupRequest = {
        companyName: this.signupForm.value.companyName,
        address: this.signupForm.value.address || undefined,
        addressLine2: this.signupForm.value.addressLine2 || undefined,
        city: this.signupForm.value.city || undefined,
        state: this.signupForm.value.state || undefined,
        zip: this.signupForm.value.zip || undefined,
        phone: this.signupForm.value.phone || undefined,
        email: this.signupForm.value.email || undefined,
        adminEmail: this.signupForm.value.adminEmail,
        adminPassword: this.signupForm.value.adminPassword,
        adminFirstName: this.signupForm.value.adminFirstName || undefined,
        adminLastName: this.signupForm.value.adminLastName || undefined
      };

      // Make the API call using AuthService
      this.authService.signup(signupData)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.snackBar.open(
              `Company "${response.companyName}" created successfully! You are now logged in.`,
              'Close',
              {
                duration: 8000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['success']
              }
            );
            this.signupForm.reset();

            // Use AuthService to store authentication data consistently
            this.authService.setAuthData(response.accessToken);

            // Redirect to quotes list since user is now authenticated
            this.router.navigate(['/quotes']);
          },
          error: (error: ApiError) => {
            this.loading = false;
            console.error('Signup error:', error);

            // Use the error message from the API service
            this.snackBar.open(error.message, 'Close', {
              duration: 8000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['error']
            });
          }
        });
    } else {
      this.snackBar.open('Please fill in all required fields correctly.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['warning']
      });
    }
  }

  getErrorMessage(fieldName: string): string {
    const field = this.signupForm.get(fieldName);
    if (field?.hasError('required')) {
      return `${this.getFieldDisplayName(fieldName)} is required`;
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (field?.hasError('minlength')) {
      return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors?.['minlength'].requiredLength} characters`;
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      companyName: 'Company Name',
      address: 'Address',
      addressLine2: 'Address Line 2',
      city: 'City',
      state: 'State',
      zip: 'ZIP Code',
      phone: 'Phone',
      email: 'Company Email',
      adminEmail: 'Admin Email',
      adminPassword: 'Admin Password',
      adminFirstName: 'Admin First Name',
      adminLastName: 'Admin Last Name'
    };
    return displayNames[fieldName] || fieldName;
  }
}
